import React from 'react';
import { useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router';
import { createUseStyles } from 'features/sharedModules/styles/components/styles.js';
import classNames from 'classnames';
import * as R from 'ramda';

import { i18n } from 'i18n/index.js';
import SavingRoboLooking from 'assets/saving-robo-icons/looking.js';
import { usePrevRoute } from 'features/sharedModules/router/components/usePrevRoute.js';
import { useNextRoute } from 'features/sharedModules/router/components/useNextRoute.js';
import sessionSelectors from 'features/shared/services/session/selectors.js';
import Button, { ButtonType } from 'features/shared/components/button/index';
import Navigation from 'features/shared/components/screenLayout/navigation';
import Icon from 'features/shared/components/icon/index';
import { TextFontSizes } from 'features/shared/constants/fonts.js';
import { Colors } from 'features/shared/constants/colors';
import SlickCarousel from 'features/shared/components/slickCarousel/index.js';
import ExternalIcon from 'features/shared/components/icon/externalIcon.js';
import Layout from 'features/shared/components/screenLayout/layout.js';
import Page from 'features/shared/components/screenLayout/page.js';
import Footer from 'features/shared/components/screenLayout/footer.js';

const useStyles = createUseStyles(theme => {
  return {
    title: {
      fontFamily: theme.headerFontFamily,
      fontSize: TextFontSizes.xLargePx,
      lineHeight: TextFontSizes.xLargeLineHeightPx,
      textAlign: 'center',
      paddingTop: '18px',
      color: Colors.grayDark
    },
    text: {
      fontFamily: theme.textFontFamily,
      fontSize: TextFontSizes.smallPx,
      lineHeight: TextFontSizes.smallLineHeightPx,
      textAlign: 'center',
      paddingTop: '19px'
    },
    card: {
      maxWidth: '320px',
      minHeight: '280px',
      background: Colors.white,
      boxShadow:
        '0px 10px 10px ' +
        Colors.listShadowColor +
        ', 0px 10px 10px ' +
        Colors.listShadowColor +
        ', 0px 10px 10px ' +
        Colors.listShadowColor +
        ', 0px 10px 10px ' +
        Colors.listShadowColor +
        ', 0px 10px 10px ' +
        Colors.listShadowColor +
        ', 0px 4px 3px ' +
        Colors.listShadowColor,
      borderRadius: '15px',
      margin: 'auto',
      height: '100%'
    },
    cardContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      justifyContent: 'center'
    },
    cardTitle: {
      fontFamily: theme.subTitleFontFamily,
      fontSize: TextFontSizes.largePx,
      lineHeight: TextFontSizes.largeLineHeightPx,
      padding: '0 17px',
      color: Colors.grayDark
    },
    cardText: {
      fontFamily: theme.textFontFamily,
      fontSize: TextFontSizes.smallPx,
      lineHeight: TextFontSizes.smallLineHeightPx,
      padding: '17px'
    },
    layoutColumn: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center'
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      marginTop: '50px'
    },
    coinsPadding: {
      paddingBottom: '23px',
      paddingTop: '32px'
    },
    moneyPadding: {
      paddingBottom: '20px',
      paddingTop: '22px'
    },
    cardContainer: {
      padding: '14px',
      height: '100%'
    }
  };
});

const WhatIsBufferScreen = () => {
  const classes = useStyles();
  const history = useHistory();
  const { customerId } = useParams();

  const prevRoute = usePrevRoute();
  const nextRoute = useNextRoute();

  const translations = useSelector(sessionSelectors.getTranslationsConfig);
  const config = useSelector(sessionSelectors.getConfig);

  return (
    <Layout>
      <Navigation prevRoute={prevRoute} customerId={customerId}>
        {i18n('whatIsBufferScreen.naviTitle', translations)}
      </Navigation>

      <Page>
        <div className={classes.content}>
          <SavingRoboLooking />

          <span className={classes.title}>
            {i18n('whatIsBufferScreen.title', translations)}
          </span>
          <span className={classes.text}>
            {i18n('whatIsBufferScreen.text_1', translations)}
          </span>

          <span className={classes.text}>
            {i18n('whatIsBufferScreen.text_2', translations)}
          </span>
        </div>

        <SlickCarousel>
          <div>
            <div className={classes.cardContainer}>
              <div className={classes.card}>
                <div
                  className={classNames(
                    classes.cardContent,
                    classes.coinsPadding
                  )}
                >
                  <ExternalIcon
                    type={R.path(['whatIsBuffer', 'card_1Image'], config)}
                  />
                </div>
                <div className={classes.layoutColumn}>
                  <span className={classes.cardTitle}>
                    {i18n('whatIsBufferScreen.card_1.title', translations)}
                  </span>
                  <span className={classes.cardText}>
                    {i18n('whatIsBufferScreen.card_1.text', translations)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className={classes.cardContainer}>
              <div className={classes.card}>
                <div
                  className={classNames(
                    classes.cardContent,
                    classes.moneyPadding
                  )}
                >
                  <Icon type="money" />
                </div>

                <div className={classes.layoutColumn}>
                  <span className={classes.cardTitle}>
                    {i18n('whatIsBufferScreen.card_2.title', translations)}
                  </span>

                  <span className={classes.cardText}>
                    {i18n('whatIsBufferScreen.card_2.text', translations)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </SlickCarousel>

        <Footer>
          {nextRoute && (
            <Button
              type={ButtonType.primary}
              onClick={() => {
                history.push(nextRoute.build(customerId));
              }}
            >
              {i18n('shared.continue', translations)}
            </Button>
          )}
        </Footer>
      </Page>
    </Layout>
  );
};

export default WhatIsBufferScreen;
